import React, { useState, useEffect } from "react";
import {
  Button,
  Input,
  Label,
  Table,
  Badge,
  Spinner,
  Collapse,
} from "reactstrap";
import axios from "axios";
import swal from "sweetalert";
import { API_TULUFALI, API_TIMEOUT, GET_DESA } from "../../../utils";
import s from "./UploadPenerima.module.scss";

const CSV_COLUMNS = [
  { key: "nik", label: "NIK", required: true, contoh: "3271234567890123" },
  { key: "nama", label: "Nama", required: true, contoh: "Ahmad" },
  { key: "dob", label: "Tanggal Lahir", required: true, contoh: "1990-01-15" },
  { key: "gender", label: "Jenis Kelamin", required: false, contoh: "Laki-laki" },
  { key: "nilaiUang", label: "Nilai Uang", required: true, contoh: "500000" },
  { key: "desa", label: "Desa", required: true, contoh: "Busalangga" },
  { key: "kecamatan", label: "Kecamatan", required: true, contoh: "Rote Barat Daya" },
  { key: "noKK", label: "No KK", required: false, contoh: "3271234567890124" },
  { key: "catatan", label: "Catatan", required: false, contoh: "-" },
  { key: "status", label: "Status", required: false, contoh: "Hijau" },
  { key: "kelompok", label: "Kelompok", required: false, contoh: "-" },
];

const parseCSVLine = (line, delimiter = ",") => {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') inQuotes = !inQuotes;
    else if (c === delimiter && !inQuotes) {
      result.push(current.trim().replace(/^"|"$/g, ""));
      current = "";
    } else current += c;
  }
  result.push(current.trim().replace(/^"|"$/g, ""));
  return result;
};

const parseCSV = (text) => {
  const raw = String(text).replace(/^\uFEFF/, "").trim();
  const lines = raw.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], headers: [] };
  const firstLine = lines[0];
  const delimiter = firstLine.includes(";") && !firstLine.includes(",") ? ";" : ",";
  const headers = parseCSVLine(lines[0], delimiter);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i], delimiter);
    const obj = {};
    headers.forEach((h, idx) => (obj[h] = values[idx] != null ? String(values[idx]).trim() : ""));
    obj._values = values;
    rows.push(obj);
  }
  return { rows, headers };
};

const DOB_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const VALID_GENDER = ["LAKI-LAKI", "PEREMPUAN"];

const UploadPenerimaPage = () => {
  const currentYear = new Date().getFullYear();
  const [tahun, setTahun] = useState(String(currentYear - 1));
  const [bantuan, setBantuan] = useState("");
  const [listBantuan, setListBantuan] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [listDesa, setListDesa] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [existingNikInDb, setExistingNikInDb] = useState(new Set());
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);

  const validDesaKec = React.useMemo(() => {
    const set = new Set();
    listDesa.forEach((d) => {
      const kec = String(d.kecamatan ?? d.Kecamatan ?? "").trim().toUpperCase();
      const des = String(d.desa ?? d.Desa ?? "").trim().toUpperCase();
      if (kec && des) set.add(`${kec}|${des}`);
    });
    return set;
  }, [listDesa]);

  const listTahun = Array.from(
    { length: Math.max(0, currentYear - 1 - 2018 + 1) },
    (_, i) => 2018 + i
  )
  .filter((y) => y < currentYear)
  .sort((a, b) => b - a);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setCurrentUser(user);
  }, []);

  useEffect(() => {
    axios.get(GET_DESA, { timeout: API_TIMEOUT })
      .then((res) => setListDesa(res.data?.data || []))
      .catch(() => setListDesa([]));
  }, []);

  const duplicateNikList = React.useMemo(() => {
    const count = {};
    csvData.forEach((row, idx) => {
      const nik = String(row.nik || "").trim();
      if (nik) {
        if (!count[nik]) count[nik] = [];
        count[nik].push(idx + 1);
      }
    });
    return Object.entries(count).filter(([, rows]) => rows.length > 1);
  }, [csvData]);

  const nikSignature = csvData.map((r) => String(r.nik || "").trim()).join("|");
  useEffect(() => {
    if (!bantuan || !tahun || csvData.length === 0) {
      setExistingNikInDb(new Set());
      setCheckingExisting(false);
      return;
    }
    const nikList = csvData
      .map((r) => String(r.nik || "").trim())
      .filter((n) => n.length === 16 && /^\d{16}$/.test(n));
    if (nikList.length === 0) {
      setExistingNikInDb(new Set());
      setCheckingExisting(false);
      return;
    }
    setCheckingExisting(true);
    axios
      .post(API_TULUFALI + "databantuan/check-existing", { nikList, bantuan, tahun }, { timeout: API_TIMEOUT })
      .then((res) => {
        const data = res.data?.data || [];
        setExistingNikInDb(new Set(data));
      })
      .catch(() => setExistingNikInDb(new Set()))
      .finally(() => setCheckingExisting(false));
  }, [bantuan, tahun, nikSignature]);

  useEffect(() => {
    const errors = {};
    csvData.forEach((row, rowIdx) => {
      const nama = String(row.nama || "").trim();
      if (!nama) errors[`${rowIdx}-nama`] = "Nama wajib diisi";
      const nik = String(row.nik || "").trim();
      if (!nik) errors[`${rowIdx}-nik`] = "NIK wajib diisi";
      else if (nik.length !== 16 || !/^\d{16}$/.test(nik)) {
        errors[`${rowIdx}-nik`] = "NIK harus 16 digit angka";
      } else {
        const dup = duplicateNikList.find(([n]) => n === nik);
        if (dup) errors[`${rowIdx}-nik`] = `NIK duplikat di baris: ${dup[1].join(", ")}`;
      }
      const noKK = String(row.noKK || "").trim();
      if (noKK && (noKK.length !== 16 || !/^\d{16}$/.test(noKK))) {
        errors[`${rowIdx}-noKK`] = "No KK harus 16 digit angka (kosongkan jika tidak ada)";
      }
      const dob = String(row.dob || "").trim();
      if (!dob) errors[`${rowIdx}-dob`] = "Tanggal lahir wajib diisi";
      else if (!DOB_REGEX.test(dob)) {
        errors[`${rowIdx}-dob`] = "Format: YYYY-MM-DD";
      }
      const nilaiUang = String(row.nilaiUang || "").trim();
      if (!nilaiUang) errors[`${rowIdx}-nilaiUang`] = "Nilai uang wajib diisi";
      else if (!/^\d+$/.test(nilaiUang)) errors[`${rowIdx}-nilaiUang`] = "Harus angka";
      const desa = String(row.desa || "").trim();
      const kec = String(row.kecamatan || "").trim();
      if (!desa) errors[`${rowIdx}-desa`] = "Desa wajib diisi";
      else if (!kec) errors[`${rowIdx}-kecamatan`] = "Kecamatan wajib diisi";
      else {
        const key = `${kec.toUpperCase()}|${desa.toUpperCase()}`;
        if (!validDesaKec.has(key)) {
          errors[`${rowIdx}-desa`] = "Desa/Kecamatan tidak ditemukan di database";
          errors[`${rowIdx}-kecamatan`] = "Desa/Kecamatan tidak ditemukan di database";
        }
      }
      const gender = String(row.gender || "").trim();
      if (gender && !VALID_GENDER.includes(gender.toUpperCase())) {
        errors[`${rowIdx}-gender`] = "Hanya boleh: Laki-laki atau Perempuan";
      }
      if (existingNikInDb.has(nik)) {
        errors[`${rowIdx}-nik`] = "Data sudah ada di database. Hapus baris ini atau hapus data lama terlebih dahulu.";
      }
    });
    setValidationErrors(errors);
  }, [csvData, validDesaKec, duplicateNikList, existingNikInDb]);

  useEffect(() => {
    if (!tahun) return;
    setLoading(true);
    const url = API_TULUFALI + "bantuantahun/ambil/tahun/upload";
    axios({ method: "post", url, timeout: API_TIMEOUT, data: { tahun } })
      .then((res) => {
        let data = res.data?.data || [];
        if (currentUser?.peran === "admin" && currentUser?.dinas) {
          data = data.filter((item) => item.dinas === currentUser.dinas);
        }
        setListBantuan(data);
        if (data.length > 0) {
          setBantuan((prev) => (prev && data.some((b) => b.bantuan === prev)) ? prev : data[0].bantuan);
        } else setBantuan("");
      })
      .catch(() => setListBantuan([]))
      .finally(() => setLoading(false));
  }, [tahun, currentUser?.peran, currentUser?.dinas]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result || "";
      const { rows, headers } = parseCSV(text);
      if (rows.length === 0) {
        setCsvData([]);
        setFileName(file.name);
        return;
      }
      const normalized = rows.map((row) => {
        const out = {};
        const values = row._values || [];
        CSV_COLUMNS.forEach((col, idx) => {
          const byIndex = values[idx] != null ? String(values[idx]).trim() : "";
          const headerName = headers[idx];
          const byHeader = headerName != null ? (row[headerName] ?? "") : "";
          const byKey = row[col.key] ?? row[col.label] ?? "";
          const byKeyLower = Object.keys(row).find(
            (k) => k !== "_values" && k.toLowerCase() === col.key.toLowerCase()
          );
          const byKeyMatch = byKeyLower != null ? row[byKeyLower] ?? "" : "";
          const val = byIndex || byHeader || byKey || byKeyMatch;
          out[col.key] = String(val != null && val !== "" ? val : "");
        });
        return out;
      });
      setCsvData(normalized);
      setFileName(file.name);
    };
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  const updateCell = (rowIdx, key, value) => {
    setCsvData((prev) => {
      const next = prev.map((r, i) =>
        i === rowIdx ? { ...r, [key]: value } : r
      );
      return next;
    });
  };

  const clearAllData = () => {
    setCsvData([]);
    setFileName("");
    setValidationErrors({});
    setExistingNikInDb(new Set());
  };

  const removeRow = (rowIdx) => {
    setCsvData((prev) => prev.filter((_, i) => i !== rowIdx));
  };

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  const handleSave = async () => {
    if (!bantuan || !tahun) {
      swal("Error", "Pilih Tahun dan Bantuan terlebih dahulu", "error");
      return;
    }
    if (csvData.length === 0) {
      swal("Error", "Tidak ada data untuk disimpan", "error");
      return;
    }
    if (hasValidationErrors) {
      swal("Error", "Perbaiki data yang ditandai merah sebelum menyimpan", "error");
      return;
    }
    const jumlah = csvData.length;
    const konfirmasi = await swal({
      title: "Konfirmasi Simpan",
      text: `Anda akan menambahkan ${jumlah} penerima untuk bantuan "${bantuan}" tahun ${tahun}. Lanjutkan?`,
      icon: "info",
      buttons: ["Batal", "Simpan"],
    });
    if (!konfirmasi) return;
    setSaving(true);
    const payload = csvData.map((r) => ({
      ...r,
      tahun,
      bantuan,
      idIndividu: r.idIndividu || "",
      idKeluarga: r.idKeluarga || "",
    }));
    try {
      const res = await axios.post(
        API_TULUFALI + "databantuan/upload-bulk",
        { data: payload },
        { timeout: API_TIMEOUT }
      );
      if (res.data?.success === 1) {
        swal("Sukses", `Berhasil menyimpan ${payload.length} penerima`, "success");
        clearAllData();
        setBantuan("");
        setTahun(String(currentYear - 1));
      } else {
        swal("Gagal", res.data?.message || "Gagal menyimpan", "error");
      }
    } catch (err) {
      swal("Gagal", err.response?.data?.message || err.message || "Gagal menyimpan", "error");
    } finally {
      setSaving(false);
    }
  };

  const isColAuto = (key) => ["nik", "noKK", "nama"].includes(key);

  return (
    <div className={`animated fadeIn ${s.pageWrap}`}>
      <div className={s.heroSection}>
        <h2>Upload Penerima Bantuan</h2>
        <p>Unggah data penerima dalam format CSV sesuai kolom yang ditentukan.</p>
      </div>

      <div className={s.sectionCard}>
        <div
          className={`${s.cardHeader} ${s.formatToggle}`}
          onClick={() => setFormatOpen(!formatOpen)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setFormatOpen(!formatOpen)}
        >
          <span>Format Kolom CSV</span>
          <Button color="link" size="sm" className="p-0">
            <i className={`fa fa-chevron-${formatOpen ? "up" : "down"}`} />
          </Button>
        </div>
        <Collapse isOpen={formatOpen}>
          <div className={s.cardBody}>
            <p className="text-muted small mb-3">
              Kolom CSV harus sesuai urutan berikut. Baris pertama adalah header.
            </p>
            <Table size="sm" bordered className={s.formatTable}>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Nama Kolom</th>
                  <th>Wajib</th>
                  <th>Contoh</th>
                </tr>
              </thead>
              <tbody>
                {CSV_COLUMNS.map((col, i) => (
                  <tr key={col.key}>
                    <td>{i + 1}</td>
                    <td><code>{col.key}</code></td>
                    <td>{col.required ? "Ya" : "Tidak"}</td>
                    <td>{col.contoh}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="text-muted small mb-3">
              Format tanggal lahir: YYYY-MM-DD (contoh: 1990-01-15). Nilai uang berupa angka tanpa pemisah.
            </p>
            <p className="text-muted small mb-3">
              <strong>Jenis Kelamin (gender):</strong> Hanya menerima nilai <code>Laki-laki</code> atau <code>Perempuan</code> (kosongkan jika tidak diisi).
            </p>
            <Button
              color="secondary"
              size="sm"
              onClick={() => {
                const header = CSV_COLUMNS.map((c) => c.key).join(",");
                const sample = CSV_COLUMNS.map((c) => c.contoh).join(",");
                const blob = new Blob([header + "\n" + sample], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "template_penerima.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <i className="fa fa-download mr-2" />
              Unduh Template CSV
            </Button>
          </div>
        </Collapse>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>Unggah & Verifikasi</div>
        <div className={s.cardBody}>
          <div className={s.controlBar}>
            <div className={s.formGroup}>
              <Label>Tahun</Label>
              <Input
                type="select"
                value={tahun}
                onChange={(e) => setTahun(e.target.value)}
              >
                <option value="">Pilih Tahun</option>
                {listTahun.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Input>
            </div>
            <div className={s.formGroup}>
              <Label>Bantuan</Label>
              <Input
                type="select"
                value={bantuan}
                onChange={(e) => setBantuan(e.target.value)}
                disabled={loading}
              >
                <option value="">Pilih Bantuan</option>
                {listBantuan.map((item) => (
                  <option key={item.id} value={item.bantuan}>
                    {item.bantuan}
                  </option>
                ))}
              </Input>
            </div>
            <div className={s.formGroup}>
              <Label>File CSV</Label>
              <Input type="file" accept=".csv" onChange={handleFileChange} />
              {fileName && (
                <Badge color="info" className="ml-2 mt-1">
                  {fileName} ({csvData.length} baris)
                </Badge>
              )}
            </div>
          </div>

          {csvData.length > 0 && (
            <>
              {duplicateNikList.length > 0 && (
                <div className={s.duplicateWarning}>
                  <strong>NIK Duplikat Ditemukan</strong>
                  <p className="mb-1 mt-1">NIK berikut muncul lebih dari satu kali:</p>
                  <ul>
                    {duplicateNikList.map(([nik, rows]) => (
                      <li key={nik}>
                        NIK <code>{nik}</code> pada baris: {rows.join(", ")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {existingNikInDb.size > 0 && (
                <div className={`${s.duplicateWarning} ${s.existingInDb}`}>
                  <strong>Data Sudah Ada di Database</strong>
                  <p className="mb-1 mt-1">
                    NIK berikut sudah terdaftar untuk bantuan &quot;{bantuan}&quot; tahun {tahun}. Hapus baris tersebut atau hapus data lama di database terlebih dahulu sebelum menyimpan.
                  </p>
                  <ul>
                    {Array.from(existingNikInDb).map((nik) => (
                      <li key={nik}>NIK <code>{nik}</code></li>
                    ))}
                  </ul>
                </div>
              )}
              {hasValidationErrors && (
                <div className="alert alert-warning py-2 mb-3">
                  <small>
                    Terdapat {Object.keys(validationErrors).length} sel bermasalah (ditandai merah). Perbaiki terlebih dahulu sebelum menyimpan.
                  </small>
                </div>
              )}
              <div className={s.dataTableWrap}>
                <div className={s.dataTableScroll}>
                  <Table bordered size="sm" hover className={s.dataTable}>
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        {CSV_COLUMNS.map((col) => (
                          <th
                            key={col.key}
                            className={isColAuto(col.key) ? s.colAuto : s.colFixed}
                          >
                            {col.label}
                          </th>
                        ))}
                        <th style={{ width: 80 }}>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          <td>{rowIdx + 1}</td>
                          {CSV_COLUMNS.map((col) => {
                            const errKey = `${rowIdx}-${col.key}`;
                            const hasError = validationErrors[errKey];
                            const val = String(row[col.key] ?? "");
                            const baseCh = col.key === "nama" ? 20 : 16;
                            const minCh = isColAuto(col.key) ? Math.min(60, Math.max(baseCh, val.length + 1)) : undefined;
                            return (
                              <td
                                key={`${rowIdx}-${col.key}`}
                                title={hasError || undefined}
                                className={isColAuto(col.key) ? s.colAuto : s.colFixed}
                                style={minCh ? { minWidth: `${minCh}ch` } : undefined}
                              >
                                <Input
                                  type="text"
                                  value={val}
                                  onChange={(e) => updateCell(rowIdx, col.key, e.target.value)}
                                  className={`${s.cellInput} ${hasError ? s.hasError : ""}`}
                                />
                              </td>
                            );
                          })}
                          <td>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => removeRow(rowIdx)}
                            >
                              Hapus
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              <div className={s.actionBar}>
                <Button
                  color="primary"
                  onClick={handleSave}
                  disabled={saving || !bantuan || hasValidationErrors || checkingExisting}
                >
                  {(saving || checkingExisting) ? <Spinner size="sm" className="mr-2" /> : null}
                  {checkingExisting ? "Memeriksa..." : saving ? "Menyimpan..." : "Simpan ke Database"}
                </Button>
                <Button color="secondary" onClick={clearAllData}>
                  Hapus Semua Data
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPenerimaPage;
